<script setup lang="ts">
/*
 * Types of modals
 * - (HTML dialog) dialog, default behavior is more like a tooltip or notification
 * - (HTML dialog) modal - should block every interaction with the rest of the page
 * - panel - variant of modal that slides in from aside
 */

type Props = {
  name?: string
  dialogType?: 'dialog' | 'modal' | 'fullscreen' | 'from-left' | 'from-right' | 'from-top' | 'from-bottom' | 'responsive'
}

const props = withDefaults(defineProps<Props>(), {
  name: 'dialog',
  dialogType: 'modal',
})

const emit = defineEmits(['close'])

const { dialog, settings, open, close } = useUiDialog(props.name, {
  type: props.dialogType,
})

// Local element reference for this specific instance. The mounted instance is
// always the authoritative one, so actuating this element (instead of the
// shared `dialog` state) is immune to async mount/unmount ordering during SPA
// navigation.
const dialogEl = ref<HTMLDialogElement | null>(null)

const syncOpenState = (isOpen?: boolean) => {
  const el = dialogEl.value

  if (!el) {
    return
  }

  if (isOpen && !el.open) {
    if (settings.value.type === 'dialog') {
      el.show()
    }
    else {
      el.showModal()
    }
  }
  else if (!isOpen && el.open) {
    el.close()
  }
}

watch(() => settings.value.isOpen, syncOpenState)

onMounted(() => {
  // Reconcile a freshly mounted element with the shared open state (e.g. when a
  // dialog was opened before its Lazy component finished mounting).
  syncOpenState(settings.value.isOpen)
})

const dialogName = computed(() => {
  return `dialog--${useDash(props.name)}`
})

const closeDialog = () => {
  window.scrollTo({
    top: settings.value.top,
    behavior: 'auto',
  })

  close()

  emit('close')
}

defineExpose({
  dialogEl,
  open,
  close,
})
</script>

<template>
  <dialog
    v-bind="$attrs"
    :ref="(el) => { dialogEl = el as HTMLDialogElement; if (el) dialog = el as HTMLDialogElement }"
    class="dialog"
    :class="`dialog--${dialogType}`"
    @click.self="closeDialog"
    @close="closeDialog"
  >
    <!-- <form
      :id="dialogName"
      class="dialog__form"
      method="dialog" /> -->

    <div class="dialog__header">
      <slot
        name="dialogHeader"
        v-bind="{ dialogName, open, close }"
      />
      <slot
        name="close"
        v-bind="{ dialogName, open, close }"
      >
        <button
          type="button"
          class="dialog__close"
          :aria-label="$t('Close')"
          @click="close"
        >
          <NuxtIcon name="icon:close" />
        </button>
        <!-- <button
          type="submit"
          class="dialog__close"
          :form="dialogName">
          <NuxtIcon name="icon:close" />
        </button> -->
      </slot>
    </div>

    <div class="dialog__content">
      <slot v-bind="{ open, close }" />
    </div>

    <div
      v-if="$slots.dialogFooter"
      class="dialog__footer"
    >
      <slot
        name="dialogFooter"
        v-bind="{ dialogName, open, close }"
      />
    </div>
  </dialog>
</template>

<style>
@import url("~layers/components/Ui/Dialog.css");
</style>
